// client/src/utils/chunkRecovery.ts
// Detects stale chunk/dynamic import failures and performs intelligent recovery

import { captureMessage } from '@/lib/sentry';
import { isInCacheLoop, recordChunkError, performFullCacheRecovery } from './cacheRecovery';
import { healthMonitor } from '@/services/healthMonitoring';
import { analyticsCollector } from '@/services/analyticsCollector';

// Track recovery attempts to prevent infinite loops
const recoveryAttempts = new Map<string, number>();
const MAX_RECOVERY_ATTEMPTS = 2;
const RECOVERY_WINDOW_MS = 60000; // 1 minute

function isChunkErrorMessage(msg: string): boolean {
  if (!msg) return false;
  const m = msg.toLowerCase();
  return (
    m.includes('chunkloaderror') ||
    m.includes('loading chunk') ||
    m.includes('failed to fetch dynamically imported module') ||
    m.includes('importing a module script failed') ||
    m.includes('unexpected token') || // stale minimized JS
    m.includes('syntax error') ||
    m.includes('cannot find module') ||
    m.includes('module not found') ||
    // m.includes('failed to fetch') || // too generic (API/network errors)
    // m.includes('network error') || // too generic (API/network errors)
    m.includes('mime type') || // Invalid MIME type for JS
    m.includes('unable to preload css') // Vite CSS preload 404 after a stale deploy (NODE-CD)
  );
}

function canAttemptRecovery(source: string): boolean {
  const now = Date.now();
  const attempts = recoveryAttempts.get(source) || 0;

  // Clean up old attempts outside the window
  if (attempts > 0) {
    const lastAttemptTime = (recoveryAttempts as any).get(`${source}_time`) || 0;
    if (now - lastAttemptTime > RECOVERY_WINDOW_MS) {
      recoveryAttempts.delete(source);
      recoveryAttempts.delete(`${source}_time`);
      return true;
    }
  }

  return attempts < MAX_RECOVERY_ATTEMPTS;
}

function tryReloadOnce(source: string) {
  const startTime = Date.now();

  // Record this error for loop detection
  recordChunkError();

  // Track in health monitor
  healthMonitor.recordChunkLoad(source, false, 0);

  // Check if we're in a cache loop (repeated errors)
  if (isInCacheLoop()) {
    console.error('[ChunkRecovery] Detected cache loop - performing full cache recovery');

    // Log to Sentry
    captureMessage('Chunk recovery cache loop detected - performing full recovery', 'error', {
      source,
      userAgent: navigator.userAgent,
      url: window.location.href,
    });

    // Track analytics
    analyticsCollector.trackRecoveryAttempt(source, false, Date.now() - startTime);

    // Perform full cache recovery
    performFullCacheRecovery().catch(err => {
      console.error('[ChunkRecovery] Cache recovery failed:', err);
      window.location.reload();
    });
    return;
  }

  // Check if we've already attempted recovery for this specific error
  const reloadKey = `chunkReload_${source}`;
  const lastReloadTime = parseInt(sessionStorage.getItem(reloadKey) || '0', 10);
  const now = Date.now();
  const timeSinceLastReload = now - lastReloadTime;

  // If we reloaded less than 5 seconds ago for the same source, don't reload again
  if (lastReloadTime > 0 && timeSinceLastReload < 5000) {
    console.error(`[ChunkRecovery] Already attempted reload for "${source}" ${timeSinceLastReload}ms ago`);

    captureMessage('Chunk recovery reload loop detected', 'error', {
      source,
      timeSinceLastReload,
      userAgent: navigator.userAgent,
      url: window.location.href,
    });

    analyticsCollector.trackRecoveryAttempt(source, false, Date.now() - startTime);
    return;
  }

  // Record this reload attempt
  sessionStorage.setItem(reloadKey, now.toString());

  // Expected stale-deploy chunk failures are recovered by reload. Do not send a
  // Sentry issue for every successful recovery; only report cache/reload loops.

  // Track analytics
  analyticsCollector.trackRecoveryAttempt(source, true, Date.now() - startTime);

  try {
    // Let the SW know we want the freshest files
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
  } catch {}

  console.warn(`[ChunkRecovery] Auto-reloading due to ${source}`);

  // Give Sentry time to send the message before reloading
  setTimeout(() => {
    try { (window as any).location.reload(true); } catch { window.location.reload(); }
  }, 100);
}

function isFirstPartyScriptUrl(url: string): boolean {
  if (!url) return false;
  try {
    const scriptUrl = new URL(url, window.location.href);
    return scriptUrl.origin === window.location.origin;
  } catch {
    return false;
  }
}

export function installChunkLoadRecovery() {
  if ((window as any).__chunkRecoveryInstalled) return;
  (window as any).__chunkRecoveryInstalled = true;
  // Script load errors - catches synchronous errors during script execution
  window.addEventListener('error', (e: ErrorEvent) => {
    const msg = (e.message || '').toString();
    const filename = (e.filename || '').toString();
    const isScript = !!(filename && filename.endsWith('.js'));
    const isFirstPartyScriptError = isScript && isFirstPartyScriptUrl(filename);
    if (isFirstPartyScriptError || isChunkErrorMessage(msg)) {
      e.preventDefault();
      console.error('[ChunkRecovery] Detected chunk/script error, attempting recovery');
      tryReloadOnce(isFirstPartyScriptError ? `script error: ${filename}` : `error: ${msg}`);
    }
  }, { capture: true });

  // Dynamic import/unhandled promise rejections - catches async module loading failures
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    try {
      const reason: any = e.reason;
      const msg = (reason?.message || reason?.toString?.() || '').toString();
      if (isChunkErrorMessage(msg)) {
        e.preventDefault();
        console.error('[ChunkRecovery] Detected chunk loading error in promise rejection, attempting recovery');
        tryReloadOnce(`unhandledrejection: ${msg}`);
      }
    } catch (err) {
    }
  });

  // Monitor for failed script loads (network errors, 404s, etc)
  window.addEventListener('error', (e: Event) => {
    if (e instanceof ErrorEvent) return; // Already handled above

    const target = e.target as HTMLScriptElement | null;
    if (target?.tagName === 'SCRIPT') {
      const src = target.src;
      if (!isFirstPartyScriptUrl(src)) {
        return;
      }
      console.error('[ChunkRecovery] Script failed to load:', src);
      tryReloadOnce(`script load failed: ${src}`);
    }
  }, { capture: true });
}

// Helper for ErrorBoundary to call directly if needed
export function attemptChunkRecoveryOnError(error: any): boolean {
  try {
    const msg = (error as any)?.message || (error as any)?.toString?.() || '';
    if (isChunkErrorMessage(String(msg))) {
      tryReloadOnce('error boundary');
      return true;
    }
  } catch {}
  return false;
}

